import React, { useState, useEffect, useCallback } from 'react';
import { UserCog, Plus, Trash2, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const RoleManagementTab = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [newRole, setNewRole] = useState({ name: '', permissions: [], description: '' });
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/roles');
      setRoles(data.roles || []);
      setPermissions(data.permissions || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, [apiCall]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/audit-logs?limit=20');
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchRoles();
    fetchAuditLogs();
  }, [fetchRoles, fetchAuditLogs]);

  const handleCreateRole = async () => {
    if (!newRole.name || selectedPermissions.length === 0) {
      showError('Please enter a name and select permissions');
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('post', '/api/admin/settings/roles', {
        name: newRole.name,
        permissions: selectedPermissions,
        description: newRole.description
      });
      showSuccess('Role created successfully');
      setNewRole({ name: '', permissions: [], description: '' });
      setSelectedPermissions([]);
      fetchRoles();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await apiCall('delete', `/api/admin/settings/roles/${roleId}`);
      showSuccess('Role deleted');
      fetchRoles();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permId) => {
    setSelectedPermissions(prev => 
      prev.includes(permId) 
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  return (
    <div className="space-y-6">
      {/* Create New Role */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-400" />
            Create Custom Role
          </CardTitle>
          <CardDescription className="text-gray-400">
            Define custom roles with specific permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm block mb-1">Role Name</label>
              <Input
                value={newRole.name}
                onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                placeholder="e.g., Content Manager"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">Description</label>
              <Input
                value={newRole.description}
                onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                placeholder="Role description..."
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          {/* Permissions Grid */}
          <div>
            <label className="text-gray-400 text-sm block mb-2">Select Permissions</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {permissions.map((perm) => (
                <button
                  key={perm.id}
                  onClick={() => togglePermission(perm.id)}
                  className={`p-2 rounded-lg border text-left transition-all ${
                    selectedPermissions.includes(perm.id)
                      ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {selectedPermissions.includes(perm.id) ? (
                      <CheckCircle className="w-4 h-4 text-yellow-400" />
                    ) : (
                      <Lock className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="text-xs">{perm.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleCreateRole}
            disabled={loading || !newRole.name || selectedPermissions.length === 0}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Role
          </Button>
        </CardContent>
      </Card>

      {/* Existing Roles */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <UserCog className="w-5 h-5 text-purple-400" />
            Current Roles ({roles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {roles.map((role) => (
              <div key={role.id} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-white">{role.name}</h4>
                      {role.is_system && (
                        <Badge className="bg-blue-500/20 text-blue-400">System</Badge>
                      )}
                      <Badge className="bg-gray-600">{role.user_count || 0} users</Badge>
                    </div>
                    <p className="text-sm text-gray-400">{role.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(role.permissions || []).slice(0, 5).map((perm) => (
                        <Badge key={perm} variant="outline" className="text-xs border-gray-600 text-gray-400">
                          {perm.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {role.permissions?.length > 5 && (
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                          +{role.permissions.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!role.is_system && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDeleteRole(role.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-cyan-400" />
            Admin Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {auditLogs.map((log) => (
              <div key={log.id || `${log.timestamp}-${log.action}-${log.admin_username}`} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge className={
                    log.action?.includes('DELETE') ? 'bg-red-500/20 text-red-400' :
                    log.action?.includes('CREATE') ? 'bg-green-500/20 text-green-400' :
                    log.action?.includes('ASSIGN') ? 'bg-purple-500/20 text-purple-400' :
                    'bg-gray-700 text-gray-300'
                  }>
                    {log.action}
                  </Badge>
                  <span className="text-sm text-gray-400">
                    by <span className="text-white">{log.admin_username}</span>
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
            {auditLogs.length === 0 && (
              <p className="text-gray-400 text-center py-4">No activity logs yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleManagementTab;
